import React, { useState, Fragment, useEffect, useMemo } from "react";
import EditPortalUserModal from "./EditPortalUserModal";
import DataTable from "react-data-table-component";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import API from "../../services/API";
import SweetAlert from "sweetalert2";
import {
  UserPlus,
  Users,
  ShieldCheck,
  Mail,
  Phone,
  IdCard,
  Trash2,
  Search,
  RefreshCw,
  UserCheck,
  AlertTriangle,
  Lock,
} from "lucide-react";

const GOV_NAVY = "#07172F";
const GOV_BLUE = "#0B4C9C";
const GOV_BG = "#F5F9FF";

const PortalUser = () => {
  const [state, setState] = useState({
    email: "",
    phone_no: "",
    staff_id: "",
    full_name: "",
    role: 2,
  });

  const [employee, setEmployee] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalAddUser, setModalAddUser] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    requestPortalUserList();
  }, []);

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Accept: "application/json",
    token: localStorage.getItem("token"),
  });

  const requestPortalUserList = async () => {
    setLoading(true);

    API.get("/schoolStaff/getStaff", { headers: getHeaders() })
      .then((response) => {
        if (response.data.response_code === 1000) {
          setEmployee(response.data.data || []);
        } else {
          toast.error(response.data.message, { autoClose: 5000 });
        }
      })
      .catch(() => {
        toast.error("Failed to fetch portal user list", { autoClose: 5000 });
      })
      .finally(() => setLoading(false));
  };

  const handleSubmit = async () => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!state.full_name.trim()) {
      toast.error("Full name is required", { autoClose: 3000 });
      return;
    }

    if (!emailPattern.test(state.email)) {
      toast.error("Invalid email address", { autoClose: 3000 });
      return;
    }

    setLoading(true);

    API.post(
      "/schoolStaff/registerStaff",
      {
        email: state.email,
        phone_no: state.phone_no,
        role: state.role,
        staff_id: state.staff_id,
        full_name: state.full_name,
        school_type: "",
        daerah: "",
      },
      { headers: getHeaders() }
    )
      .then((response) => {
        if (response.data.response_code === 1000) {
          requestPortalUserList();
          toggleModal();

          SweetAlert.fire({
            title: "Account Created",
            text: "Portal user account has been registered successfully.",
            icon: "success",
            confirmButtonColor: GOV_BLUE,
          });
        } else {
          toast.error(response.data.message, { autoClose: 5000 });
        }
      })
      .catch(() => {
        toast.error("Failed to create portal user", { autoClose: 5000 });
      })
      .finally(() => setLoading(false));
  };

  const handleDelete = (row) => {
    SweetAlert.fire({
      title: "Delete portal user?",
      text: `You are about to delete ${row.full_name}'s profile. This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#DC2626",
      cancelButtonColor: "#64748B",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteStaff(row);
      }
    });
  };

  const deleteStaff = (row) => {
    setLoading(true);

    API.delete("/schoolStaff/deleteStaff", {
      headers: getHeaders(),
      data: { email: row.email },
    })
      .then((response) => {
        if (response.data.response_code === 1000) {
          SweetAlert.fire({
            title: "Deleted",
            text: `${row.full_name} has been deleted successfully.`,
            icon: "success",
            confirmButtonColor: GOV_BLUE,
          });
          requestPortalUserList();
        } else {
          toast.error(response.data.message, { autoClose: 5000 });
        }
      })
      .catch(() => {
        toast.error("Failed to delete portal user profile", {
          autoClose: 5000,
        });
      })
      .finally(() => setLoading(false));
  };

  const toggleModal = () => {
    setModalAddUser(!modalAddUser);

    if (!modalAddUser) {
      setState({
        email: "",
        phone_no: "",
        full_name: "",
        role: 2,
        staff_id: "",
      });
    }
  };

  const onChange = (field, event) => {
    const fieldValue = event.target.value;

    setState((prevState) => ({
      ...prevState,
      [field]: fieldValue,
    }));
  };

  const openEditModal = () => {
    toast.info("Edit functionality not implemented yet.", {
      autoClose: 3000,
    });
  };

  const filteredUsers = useMemo(() => {
    const keyword = searchTerm.toLowerCase().trim();

    if (!keyword) return employee;

    return employee.filter((item) => {
      const fullName = String(item.full_name || "").toLowerCase();
      const email = String(item.email || "").toLowerCase();
      const phone = String(item.phone_no || "").toLowerCase();
      const staffId = String(item.staff_id || "").toLowerCase();
      const role = item.role === "1" ? "admin" : "staff officer";

      return (
        fullName.includes(keyword) ||
        email.includes(keyword) ||
        phone.includes(keyword) ||
        staffId.includes(keyword) ||
        role.includes(keyword)
      );
    });
  }, [employee, searchTerm]);

  const adminCount = useMemo(
    () => employee.filter((item) => String(item.role) === "1").length,
    [employee]
  );

  const officerCount = useMemo(
    () => employee.filter((item) => String(item.role) !== "1").length,
    [employee]
  );

  const columns = [
    {
      name: "Portal User",
      selector: (row) => row.full_name,
      sortable: true,
      grow: 2.2,
      cell: (row) => (
        <div className="spgov-user-cell">
          <div className="spgov-user-avatar">
            {(row.full_name || row.email || "U").charAt(0).toUpperCase()}
          </div>

          <div>
            <div className="spgov-user-name">{row.full_name || "-"}</div>
            <div className="spgov-user-sub">
              <Mail size={13} />
              {row.email || "-"}
            </div>
          </div>
        </div>
      ),
    },
    {
      name: "Phone",
      selector: (row) => row.phone_no,
      sortable: true,
      grow: 1.2,
      cell: (row) => (
        <div className="spgov-inline-muted">
          <Phone size={14} />
          {row.phone_no || "-"}
        </div>
      ),
    },
    {
      name: "Staff ID",
      selector: (row) => row.staff_id,
      sortable: true,
      grow: 1.1,
      cell: (row) => (
        <div className="spgov-inline-muted">
          <IdCard size={14} />
          {row.staff_id || "-"}
        </div>
      ),
    },
    {
      name: "Role",
      selector: (row) => (row.role === "1" ? "Admin" : "Staff"),
      sortable: true,
      center: true,
      width: "150px",
      cell: (row) => {
        const isAdmin = String(row.role) === "1";

        return (
          <span className={`spgov-role-badge ${isAdmin ? "admin" : "staff"}`}>
            {isAdmin ? (
              <>
                <ShieldCheck size={14} />
                Admin
              </>
            ) : (
              <>
                <UserCheck size={14} />
                Officer
              </>
            )}
          </span>
        );
      },
    },
    {
      name: "Actions",
      sortable: false,
      right: true,
      width: "150px",
      cell: (row) => {
        const isAdmin = String(row.role) === "1";

        return isAdmin ? (
          <span className="spgov-locked-pill">
            <Lock size={13} />
            Protected
          </span>
        ) : (
          <button
            type="button"
            className="spgov-delete-button"
            onClick={() => handleDelete(row)}
          >
            <Trash2 size={15} />
            Delete
          </button>
        );
      },
    },
  ];

  const customStyles = {
    table: {
      style: {
        backgroundColor: "#FFFFFF",
      },
    },
    headRow: {
      style: {
        backgroundColor: "#F5F9FF",
        borderBottom: "1px solid #DBEAFE",
        minHeight: "58px",
      },
    },
    headCells: {
      style: {
        color: GOV_BLUE,
        fontSize: "12px",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.035em",
      },
    },
    rows: {
      style: {
        minHeight: "76px",
        borderBottom: "1px solid #EEF2F7",
        fontSize: "14px",
      },
      highlightOnHoverStyle: {
        backgroundColor: "#EFF6FF",
        borderBottomColor: "#DBEAFE",
        outline: "none",
      },
    },
    pagination: {
      style: {
        borderTop: "1px solid #EEF2F7",
        padding: "12px 16px",
      },
    },
  };

  return (
    <Fragment>
      <EditPortalUserModal
        openModal={false}
        data={null}
        onSubmitSuccess={requestPortalUserList}
      />

      <style>{`
        .spgov-user-page {
          min-height: 100vh;
          padding: 28px;
          background:
            radial-gradient(circle at top left, rgba(11, 76, 156, 0.10), transparent 28%),
            radial-gradient(circle at top right, rgba(7, 23, 47, 0.08), transparent 26%),
            linear-gradient(180deg, ${GOV_BG} 0%, #EEF4FB 100%);
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          color: #0F172A;
        }

        .spgov-user-hero {
          position: relative;
          overflow: hidden;
          margin-bottom: 22px;
          padding: 30px;
          border-radius: 28px;
          color: #FFFFFF;
          background:
            linear-gradient(135deg, rgba(7, 23, 47, 0.98), rgba(11, 76, 156, 0.94)),
            radial-gradient(circle at 88% 12%, rgba(59, 130, 246, 0.28), transparent 34%);
          box-shadow: 0 26px 70px rgba(8, 27, 58, 0.18);
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 24px;
          align-items: center;
        }

        .spgov-user-hero::before {
          content: "";
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px);
          background-size: 44px 44px;
          pointer-events: none;
        }

        .spgov-user-hero > * {
          position: relative;
          z-index: 2;
        }

        .spgov-user-kicker {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          width: fit-content;
          margin-bottom: 12px;
          padding: 8px 12px;
          border-radius: 999px;
          border: 1px solid rgba(255,255,255,0.16);
          background: rgba(255,255,255,0.09);
          color: rgba(255,255,255,0.86);
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }

        .spgov-user-hero h1 {
          margin: 0;
          font-size: clamp(30px, 4vw, 48px);
          line-height: 1.05;
          font-weight: 760;
          letter-spacing: -0.03em;
        }

        .spgov-user-hero p {
          max-width: 760px;
          margin: 14px 0 0;
          color: rgba(255,255,255,0.74);
          font-size: 14.5px;
          line-height: 1.65;
        }

        .spgov-hero-actions {
          display: flex;
          flex-wrap: wrap;
          justify-content: flex-end;
          gap: 10px;
        }

        .spgov-hero-btn {
          min-height: 46px;
          border: 1px solid rgba(255,255,255,0.20);
          border-radius: 15px;
          padding: 0 17px;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(255,255,255,0.11);
          color: #FFFFFF;
          font-size: 13px;
          font-weight: 650;
          cursor: pointer;
          transition: 0.16s ease;
        }

        .spgov-hero-btn:hover {
          transform: translateY(-1px);
          background: rgba(255,255,255,0.16);
        }

        .spgov-hero-btn.primary {
          background: #FFFFFF;
          color: ${GOV_NAVY};
          border-color: #FFFFFF;
          box-shadow: 0 18px 40px rgba(0,0,0,0.14);
        }

        .spgov-stat-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 14px;
          margin-bottom: 22px;
        }

        .spgov-stat-card {
          padding: 20px;
          border-radius: 24px;
          background: rgba(255,255,255,0.95);
          border: 1px solid #DBEAFE;
          box-shadow: 0 16px 42px rgba(8, 27, 58, 0.07);
        }

        .spgov-stat-label {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #64748B;
          font-size: 12px;
          font-weight: 600;
        }

        .spgov-stat-value {
          margin-top: 9px;
          color: ${GOV_NAVY};
          font-size: 34px;
          line-height: 1;
          font-weight: 750;
          letter-spacing: -0.025em;
        }

        .spgov-stat-desc {
          margin-top: 8px;
          color: #64748B;
          font-size: 12.5px;
        }

        .spgov-panel {
          border-radius: 26px;
          background: rgba(255,255,255,0.96);
          border: 1px solid #DBEAFE;
          box-shadow: 0 18px 48px rgba(8, 27, 58, 0.08);
          overflow: hidden;
        }

        .spgov-panel-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 22px 24px;
          border-bottom: 1px solid #E5EDF7;
          background: linear-gradient(180deg, #FFFFFF 0%, #F8FBFF 100%);
        }

        .spgov-panel-header h5 {
          margin: 0;
          color: ${GOV_NAVY};
          font-weight: 700;
          letter-spacing: -0.01em;
        }

        .spgov-panel-header p {
          margin: 5px 0 0;
          color: #64748B;
          font-size: 13px;
        }

        .spgov-search-row {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .spgov-search-box {
          position: relative;
        }

        .spgov-search-box svg {
          position: absolute;
          top: 50%;
          left: 13px;
          transform: translateY(-50%);
          color: #94A3B8;
        }

        .spgov-search-input {
          min-width: 330px;
          height: 44px;
          border-radius: 14px;
          border: 1px solid #DBEAFE;
          padding: 0 14px 0 40px;
          color: #0F172A;
          font-size: 14px;
          outline: none;
        }

        .spgov-search-input:focus {
          border-color: ${GOV_BLUE};
          box-shadow: 0 0 0 4px rgba(11,76,156,0.10);
        }

        .spgov-clear-btn {
          height: 44px;
          border-radius: 14px;
          border: 1px solid #DBEAFE;
          background: #FFFFFF;
          color: #475569;
          padding: 0 16px;
          font-size: 13px;
          font-weight: 600;
        }

        .spgov-table-badge {
          min-height: 34px;
          padding: 7px 12px;
          border-radius: 999px;
          background: #EEF6FF;
          color: ${GOV_BLUE};
          font-size: 12px;
          font-weight: 650;
        }

        .spgov-user-cell {
          display: flex;
          align-items: center;
          gap: 13px;
          padding: 14px 0;
        }

        .spgov-user-avatar {
          width: 44px;
          height: 44px;
          border-radius: 15px;
          display: grid;
          place-items: center;
          background: #EEF6FF;
          color: ${GOV_BLUE};
          font-size: 15px;
          font-weight: 700;
          flex: 0 0 auto;
        }

        .spgov-user-name {
          color: ${GOV_NAVY};
          font-size: 14px;
          font-weight: 700;
          margin-bottom: 4px;
        }

        .spgov-user-sub,
        .spgov-inline-muted {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #64748B;
          font-size: 12.5px;
          font-weight: 500;
        }

        .spgov-role-badge {
          min-height: 32px;
          border-radius: 999px;
          padding: 6px 11px;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          font-size: 12px;
          font-weight: 650;
        }

        .spgov-role-badge.admin {
          background: #EEF6FF;
          color: ${GOV_BLUE};
          border: 1px solid #DBEAFE;
        }

        .spgov-role-badge.staff {
          background: #F1F5F9;
          color: #334155;
          border: 1px solid #E2E8F0;
        }

        .spgov-delete-button {
          min-height: 36px;
          border-radius: 12px;
          border: 1px solid #FECACA;
          background: #FEF2F2;
          color: #B91C1C;
          padding: 0 13px;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          font-size: 12px;
          font-weight: 650;
          cursor: pointer;
        }

        .spgov-locked-pill {
          min-height: 34px;
          border-radius: 999px;
          padding: 6px 11px;
          background: #F8FAFC;
          color: #64748B;
          border: 1px solid #E2E8F0;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 600;
        }

        .spgov-empty-state {
          min-height: 260px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          gap: 10px;
          text-align: center;
          color: #64748B;
        }

        .spgov-empty-icon {
          width: 66px;
          height: 66px;
          border-radius: 22px;
          display: grid;
          place-items: center;
          background: #EEF6FF;
          color: ${GOV_BLUE};
          border: 1px solid #DBEAFE;
        }

        .spgov-empty-state strong {
          color: ${GOV_NAVY};
          font-size: 16px;
          font-weight: 700;
        }

        .spgov-modal .modal-content {
          border: 0;
          border-radius: 26px;
          overflow: hidden;
          box-shadow: 0 30px 90px rgba(8, 27, 58, 0.22);
        }

        .spgov-modal .modal-header {
          padding: 22px 24px;
          border-bottom: 1px solid #E5EDF7;
          background:
            radial-gradient(circle at top right, rgba(11, 76, 156, 0.10), transparent 34%),
            linear-gradient(180deg, #FFFFFF 0%, #F8FBFF 100%);
        }

        .spgov-modal .modal-body {
          padding: 24px;
          background: #F8FBFF;
        }

        .spgov-modal .modal-footer {
          padding: 18px 24px;
          border-top: 1px solid #E5EDF7;
          background: #FFFFFF;
        }

        .spgov-modal-title-small {
          color: #64748B;
          font-size: 12px;
          font-weight: 600;
        }

        .spgov-modal-title-main {
          color: ${GOV_NAVY};
          font-size: 18px;
          font-weight: 700;
          letter-spacing: -0.01em;
        }

        .spgov-form-card {
          padding: 20px;
          border-radius: 22px;
          border: 1px solid #DBEAFE;
          background: #FFFFFF;
          box-shadow: 0 12px 32px rgba(8, 27, 58, 0.05);
        }

        .spgov-form-label {
          display: block;
          margin-bottom: 7px;
          color: #24364F;
          font-size: 13px;
          font-weight: 650;
        }

        .spgov-form-control {
          min-height: 46px;
          border-radius: 15px;
          border: 1px solid #DBEAFE;
          color: #0F172A;
          font-size: 14px;
          box-shadow: none;
        }

        .spgov-form-control:focus {
          border-color: ${GOV_BLUE};
          box-shadow: 0 0 0 4px rgba(11,76,156,0.10);
        }

        .spgov-btn-primary {
          min-height: 42px;
          border: 0 !important;
          border-radius: 14px !important;
          padding: 0 18px !important;
          background: ${GOV_BLUE} !important;
          color: #FFFFFF !important;
          font-size: 13px !important;
          font-weight: 650 !important;
          box-shadow: 0 12px 26px rgba(11, 76, 156, 0.22);
        }

        .spgov-btn-secondary {
          min-height: 42px;
          border-radius: 14px !important;
          padding: 0 18px !important;
          border: 1px solid #DBEAFE !important;
          background: #FFFFFF !important;
          color: #475569 !important;
          font-size: 13px !important;
          font-weight: 600 !important;
        }

        @media (max-width: 992px) {
          .spgov-user-page {
            padding: 18px;
          }

          .spgov-user-hero {
            grid-template-columns: 1fr;
          }

          .spgov-hero-actions {
            justify-content: flex-start;
          }

          .spgov-stat-grid {
            grid-template-columns: 1fr;
          }

          .spgov-panel-header {
            align-items: flex-start;
            flex-direction: column;
          }

          .spgov-search-row,
          .spgov-search-box,
          .spgov-search-input {
            width: 100%;
            min-width: 0;
          }
        }

        @media (max-width: 576px) {
          .spgov-user-page {
            padding: 12px;
          }

          .spgov-user-hero {
            padding: 20px;
            border-radius: 22px;
          }

          .spgov-user-hero h1 {
            font-size: 28px;
          }

          .spgov-panel-header {
            padding: 18px;
          }

          .spgov-modal .modal-body {
            padding: 18px;
          }
        }
      `}</style>

      <div className="spgov-user-page">
        <section className="spgov-user-hero">
          <div>
            <div className="spgov-user-kicker">
              <ShieldCheck size={14} />
              SPGO.my Access Administration
            </div>

            <h1>Portal User Management</h1>

            <p>
              Create, review and manage authorised portal users, administrative
              access roles, contact details and officer accounts for the
              government operations portal.
            </p>
          </div>

          <div className="spgov-hero-actions">
            <button
              type="button"
              className="spgov-hero-btn"
              onClick={requestPortalUserList}
              disabled={loading}
            >
              <RefreshCw size={16} />
              Refresh
            </button>

            <button
              type="button"
              className="spgov-hero-btn primary"
              onClick={toggleModal}
            >
              <UserPlus size={16} />
              Add Portal User
            </button>
          </div>
        </section>

        <section className="spgov-stat-grid">
          <div className="spgov-stat-card">
            <div className="spgov-stat-label">
              <Users size={15} />
              Total Users
            </div>
            <div className="spgov-stat-value">{employee.length}</div>
            <div className="spgov-stat-desc">Registered portal accounts</div>
          </div>

          <div className="spgov-stat-card">
            <div className="spgov-stat-label">
              <ShieldCheck size={15} />
              Administrators
            </div>
            <div className="spgov-stat-value">{adminCount}</div>
            <div className="spgov-stat-desc">Protected admin accounts</div>
          </div>

          <div className="spgov-stat-card">
            <div className="spgov-stat-label">
              <UserCheck size={15} />
              Officers
            </div>
            <div className="spgov-stat-value">{officerCount}</div>
            <div className="spgov-stat-desc">Operational user accounts</div>
          </div>
        </section>

        <section className="spgov-panel">
          <div className="spgov-panel-header">
            <div>
              <h5>Portal User Directory</h5>
              <p>
                Showing {filteredUsers.length} of {employee.length} user records.
              </p>
            </div>

            <div className="spgov-search-row">
              <div className="spgov-search-box">
                <Search size={15} />
                <input
                  className="spgov-search-input"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {searchTerm && (
                <button
                  type="button"
                  className="spgov-clear-btn"
                  onClick={() => setSearchTerm("")}
                >
                  Clear
                </button>
              )}

              <div className="spgov-table-badge">Administrator View</div>
            </div>
          </div>

          <DataTable
            columns={columns}
            data={filteredUsers}
            striped={false}
            pagination
            progressPending={loading}
            persistTableHead
            highlightOnHover
            customStyles={customStyles}
            progressComponent={
              <div className="spgov-empty-state">
                <div className="spinner-border" role="status" />
                <span>Loading portal users...</span>
              </div>
            }
            noDataComponent={
              <div className="spgov-empty-state">
                <div className="spgov-empty-icon">
                  <Users size={26} />
                </div>

                <strong>No portal users found</strong>

                <span>Create a new user or adjust your search keyword.</span>
              </div>
            }
          />
        </section>
      </div>

      <Modal
        isOpen={modalAddUser}
        toggle={toggleModal}
        centered
        modalClassName="spgov-modal"
      >
        <ModalHeader toggle={toggleModal}>
          <div>
            <div className="spgov-modal-title-small">
              New Authorised Account
            </div>
            <div className="spgov-modal-title-main">Add Portal User</div>
          </div>
        </ModalHeader>

        <ModalBody>
          <div className="spgov-form-card">
            <div className="mb-3">
              <label className="spgov-form-label">Full Name</label>
              <input
                className="form-control spgov-form-control"
                type="text"
                value={state.full_name}
                onChange={(e) => onChange("full_name", e)}
                placeholder="Enter full name"
              />
            </div>

            <div className="mb-3">
              <label className="spgov-form-label">Email Address</label>
              <input
                className="form-control spgov-form-control"
                type="email"
                value={state.email}
                onChange={(e) => onChange("email", e)}
                placeholder="name@agency.gov.my"
              />
            </div>

            <div className="mb-3">
              <label className="spgov-form-label">Phone Number</label>
              <input
                className="form-control spgov-form-control"
                type="text"
                value={state.phone_no}
                onChange={(e) => onChange("phone_no", e)}
                placeholder="Phone number"
              />
            </div>

            <div>
              <label className="spgov-form-label">Staff ID</label>
              <input
                className="form-control spgov-form-control"
                type="text"
                value={state.staff_id}
                onChange={(e) => onChange("staff_id", e)}
                placeholder="Officer / staff ID"
              />
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            className="spgov-btn-secondary"
            onClick={toggleModal}
            disabled={loading}
          >
            Cancel
          </Button>

          <Button
            className="spgov-btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Creating..." : "Create Account"}
          </Button>
        </ModalFooter>
      </Modal>

      <ToastContainer theme="colored" />
    </Fragment>
  );
};

export default PortalUser;